import React, { useState } from "react";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Link from "@material-ui/core/Link";
import Hidden from "@material-ui/core/Hidden";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useAuth } from "../../firebase/AuthProvider";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { emailRegex } from "../../constants";
import Spinner from "../shared/Spinner";
import { colors } from "../../theme";
import Button from "../shared/Button";
import LogoYfca from "./svg/logo-yfca.svg";
import LogoActadapt from "./svg/logo-actadapt.png";
import LogoGca from "./svg/logo-gca.svg";
import DefaultBackground from "./svg/register-background.svg";

interface Credentials {
  email: string;
  password: string;
}

const useStyles = makeStyles((theme) => ({
  form: {
    width: "100%",
    textAlign: "center",
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    width: "140px",
  },
  error: {
    color: colors.ERROR,
  },
  title: {
    fontSize: "32px",
    fontWeight: 600,
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: "2.66",
    letterSpacing: "-0.06px",
    marginTop: "150px",
    color: colors.TEXT_PRIMARY,
    [theme.breakpoints.down("xs")]: {
      marginTop: "0px",
    }
  },
  imgContainer: {
    position: "relative",
    width: "calc(100% - 275px)",
    top: "100px",
    [theme.breakpoints.down("sm")]: {
      width: "95%",
      marginLeft: "1%"
    },
    [theme.breakpoints.down("xs")]: {
      top: "20px",
    },
  },
  link:  {
    color:colors.LINK
  },
  sideImage: {
    backgroundImage: `url(${DefaultBackground})`,
    position: "absolute",
    height: "100%",
    width: "250px",
    left: 0,
  },
  input: {
    width: "450px",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      marginLeft: "10px"
    }
  },
  gridImage: {
    margin: "0 auto",
  },
  signin: {
    textAlign: "center",
    marginTop: "25px"
  },
}));

export default function SignInPage() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const [error, setError] = useState<boolean>(false);
  const history = useHistory();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Keeping the form's state and doing validation is done with react-hook-form's useForm hook.
  const { register, handleSubmit, errors } = useForm<Credentials>();

  async function signInClick({ email, password }: Credentials) {
    setError(false);
    setIsLoading(true);

    let redirect = false;
    try {
      await signIn(email, password);
      redirect = true;
    } catch (reason) {
      setError(true);
    } finally {
      setIsLoading(false);
    }

    // Do not redirect before all state updates have been done to prevent the
    // "Can't perform a React state update on an unmounted component." warning.
    if (redirect) history.replace({ pathname: "/" });
  }

  return (
    <>
    <Grid container sm xs={12} md={12} spacing={2}>
        <Hidden smDown>
          <Grid item md={2}>
            <Grid container item className={classes.sideImage} />
          </Grid>
        </Hidden>
        <Grid item xs={12} sm={12} md={8} container justify="center" alignItems="center">
          <Spinner isVisible={isLoading} />
          <div className={classes.title}>{t("signin.signin")}</div>

          <form
            className={classes.form}
            noValidate
            onSubmit={handleSubmit(signInClick)}
          >
            {error && (
              <Grid item xs={12}>
                <Typography>
                  <div className={classes.error}>
                    <div>{t("signin.invalidcredentials")}</div>
                  </div>
                </Typography>
              </Grid>
            )}

            <Grid item>
              <TextField
                className={classes.input}
                variant="outlined"
                margin="normal"
                required
                id="email"
                label={t("signin.email")}
                name="email"
                autoComplete="email"
                autoFocus
                inputRef={register({
                  required: true,
                  maxLength: 255,
                  pattern: emailRegex,
                })}
                error={errors.email ? true : false}
                helperText={errors.email ? t("signin.email_error") : null}
              />
            </Grid>

            <Grid item>
              <TextField
                className={classes.input}
                variant="outlined"
                margin="normal"
                required
                name="password"
                label={t("signin.password")}
                type="password"
                id="password"
                autoComplete="current-password"
                inputRef={register({ required: true })}
                error={errors.password ? true : false}
                helperText={errors.password ? t("signin.password_error") : null}
              />
            </Grid>

            <Grid className={classes.signin}>
              <Link component={RouterLink} to="/forgot-password" variant="body2" className={classes.link}>
                {t("signin.forgotpassword")}
              </Link>
            </Grid>

            <Grid item>
              <Button type="submit" className={classes.submit}>
                {t("signin.submit")}
              </Button>
            </Grid>

            <Grid item xs={12} className={classes.signin}>
              <Link component={RouterLink} to="/signup" variant="body2" className={classes.link}>
                {t("signin.noaccountyet")}
              </Link>
            </Grid>
            <Grid item xs={12} className={classes.signin}>
              <Link component={RouterLink} to="/" variant="body2" className={classes.link}>
                {t("signin.backtohome")}
              </Link>
            </Grid>
          </form>
    </Grid>
    <Grid className={classes.imgContainer} container sm xs={12} spacing={2}>
            <Grid container item xs={12} sm={4}>
              <img src={LogoYfca} style={{width: "80px"}} className={classes.gridImage} alt="logo"/>
            </Grid>
            <Grid container item xs={12} sm={4} >
              <img src={LogoGca} className={classes.gridImage} alt="logo"/>
            </Grid>
            <Grid container item xs={12} sm={4}>
              <img src={LogoActadapt} style={{width: "auto", height: "60px", marginTop: "17px"}} className={classes.gridImage} alt="logo"/>
            </Grid>
          </Grid>
        </Grid>
    </>
  );
}
